import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { ArrowUp, ArrowsOutSimple, CaretRight } from "@phosphor-icons/react";
import { Badge, Button, Flex, Text, TextArea } from "@radix-ui/themes";
import { Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { askPunttChatRoute } from "./chatRoute";
import { AssetViewerPane } from "./components/AssetViewerPane";
import { type CanvasContent } from "./components/canvas/types";
import { CanvasLoadingPane } from "./components/CanvasLoadingPane";
import { CanvasPane } from "./components/CanvasPane";
import { ChatMessage, RobotIcon } from "./components/ChatMessage";
import { InputPlusMenu } from "./components/InputPlusMenu";
import { SuggestedResponses } from "./components/SuggestedResponses";
import { TicketDetailsDrawer } from "./components/TicketDetailsDrawer";
import { TicketViewPane } from "./components/TicketViewPane";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useTicketViewData } from "./hooks/useTicketView";
import { askPunttRoute } from "./route";

import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { useAppDispatch, useAppSelector } from "../../utils/hooks";
import {
  useAskPunttChatHistory,
  useSendAskPunttMessage,
} from "../../utils/queries/askPuntt";
import { setDrawerOpen } from "../../utils/slices/ui";

const CONNECTORS = [
  { id: "sharepoint", defaultEnabled: true },
  { id: "wrike", defaultEnabled: true },
  { id: "teams", defaultEnabled: false },
] as const;

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 300_000;

const SKILL_LABELS: Record<string, string> = {
  claim_generation: "Claims Verification",
  compliance_review: "Compliance Review",
  artwork_review: "Artwork Review",
  brief_creation: "Brief Writing",
};

/** Convert chat's enabledConnectors array to Record for display */
function connectorsArrayToRecord(
  ids: string[] | undefined,
): Record<string, boolean> {
  const record: Record<string, boolean> = {};
  CONNECTORS.forEach((c) => {
    record[c.id] = ids?.includes(c.id) ?? c.defaultEnabled;
  });
  return record;
}

export default function AskPunttChat() {
  const { chatId } = askPunttChatRoute.useParams();
  const [waitingForAI, setWaitingForAI] = useState(false);

  const [reviewPolling, setReviewPolling] = useState(false);

  const {
    data: chatData,
    isLoading,
    dataUpdatedAt,
  } = useAskPunttChatHistory(chatId, {
    refetchInterval: waitingForAI
      ? POLL_INTERVAL_MS
      : reviewPolling
        ? 3000
        : false,
  });
  const sendMessage = useSendAskPunttMessage(chatId);
  const analytics = useAnalytics("ask_puntt_chat");

  const [inputMessage, setInputMessage] = useState("");
  const [localMessages, setLocalMessages] = useState<AskPunttMessage[]>([]);
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles({
    onUploadError: (err) =>
      analytics.capture("ask_puntt_upload_error", {
        chat_id: chatId,
        ...errorAnalyticsPayload(err),
      }),
  });
  const [canvasDismissed, setCanvasDismissed] = useState(false);
  const [ticketDismissed, setTicketDismissed] = useState(false);
  const [focusedComment, setFocusedComment] = useState<{
    id: string;
    x: number;
    y: number;
    page?: number;
  } | null>(null);
  const [ticketDetailsOpen, setTicketDetailsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const initialLoadRef = useRef(true);
  const knownMessageCountRef = useRef(0);
  const waitingSinceRef = useRef<number | null>(null);
  const dataAtWaitStartRef = useRef<number | null>(null);
  const prevDataUpdatedAtRef = useRef<number>(0);

  useEffect(() => {
    if (waitingForAI) {
      waitingSinceRef.current = Date.now();
    } else {
      waitingSinceRef.current = null;
      dataAtWaitStartRef.current = null;
    }
  }, [waitingForAI]);

  // Sync server messages with local state and detect completed assistant
  // responses.  We use `dataUpdatedAt` (changes on every successful refetch)
  // instead of `chatData?.messages` alone — TanStack Query's structural
  // sharing can return the same array reference when the deep content hasn't
  // changed from its perspective, which would prevent the effect from firing.
  useEffect(() => {
    if (!chatData?.messages) return;

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      const messages = chatData.messages;
      knownMessageCountRef.current = messages.length;
      setLocalMessages(messages);
      const last = messages[messages.length - 1];
      if (
        messages.length > 0 &&
        (last.role === "user" ||
          (last.role === "assistant" &&
            (last.status === "pending" || !last.content)))
      ) {
        setWaitingForAI(true);
      }
      return;
    }

    const dataChanged = dataUpdatedAt !== prevDataUpdatedAtRef.current;
    if (dataChanged) {
      prevDataUpdatedAtRef.current = dataUpdatedAt;
      setLocalMessages(chatData.messages);
      knownMessageCountRef.current = chatData.messages.length;
    }

    if (!waitingForAI) return;

    if (
      waitingSinceRef.current &&
      Date.now() - waitingSinceRef.current > POLL_TIMEOUT_MS
    ) {
      setWaitingForAI(false);
      return;
    }

    if (!dataChanged) return;

    // Skip response detection until we get fresh data after waiting started.
    // Without this, the effect fires with stale chatData (still showing the
    // previous assistant response) and immediately stops polling.
    if (
      dataAtWaitStartRef.current !== null &&
      dataUpdatedAt <= dataAtWaitStartRef.current
    ) {
      return;
    }

    const lastMsg = chatData.messages[chatData.messages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.content &&
      lastMsg.status !== "pending"
    ) {
      setWaitingForAI(false);
      dataAtWaitStartRef.current = null;
      // Reset canvas/ticket dismissed state when new response arrives
      if (lastMsg.canvasContent) {
        setCanvasDismissed(false);
      }
      if (lastMsg.viewTicketId) {
        setTicketDismissed(false);
        setReviewPolling(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdatedAt, waitingForAI]);

  useEffect(() => {
    analytics.capture("ask_puntt_chat_viewed", { chat_id: chatId });
  }, [chatId]);

  // Auto-scroll to bottom when messages or stream state changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [localMessages, waitingForAI]);

  // Detect the latest canvas content from messages
  const activeCanvas = useMemo((): {
    canvas: CanvasContent;
    skill?: string;
  } | null => {
    if (canvasDismissed) return null;
    // Find the last assistant message with canvasContent
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return {
          canvas: msg.canvasContent as CanvasContent,
          skill: msg.activeSkill ?? undefined,
        };
      }
    }
    return null;
  }, [localMessages, canvasDismissed]);

  // Check if any message has canvas content (regardless of dismissed state)
  const hasCanvasContent = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (
        msg.role === "assistant" &&
        msg.canvasContent &&
        (msg.canvasContent as CanvasContent).blocks?.length > 0
      ) {
        return true;
      }
    }
    return false;
  }, [localMessages]);

  // Detect canvas loading state (agent is generating canvas content)
  const isCanvasLoading = useMemo(() => {
    if (!waitingForAI) return false;
    const lastMsg = localMessages[localMessages.length - 1];
    return !!(lastMsg?.role === "assistant" && lastMsg.canvasLoading);
  }, [localMessages, waitingForAI]);

  // Auto-collapse sidebar when canvas opens
  const dispatch = useAppDispatch();
  const sidebarOpen = useAppSelector((state) => state.ui.drawerOpen.app);
  useEffect(() => {
    if ((activeCanvas || isCanvasLoading) && sidebarOpen) {
      dispatch(setDrawerOpen({ app: false }));
    }
  }, [activeCanvas, isCanvasLoading, sidebarOpen, dispatch]);

  // Detect the latest viewTicketId from messages
  const activeViewTicketId = useMemo((): string | null => {
    if (ticketDismissed) return null;
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.viewTicketId) return msg.viewTicketId;
    }
    return null;
  }, [localMessages, ticketDismissed]);

  // Lift ticket data to parent to determine layout mode
  const {
    ticket: viewTicket,
    comments: viewComments,
    isLoading: viewTicketLoading,
    hasContextAssets,
    contextFiles,
    latestReviewFiles,
  } = useTicketViewData(activeViewTicketId);

  const use3PaneLayout = !!activeViewTicketId && hasContextAssets;

  const handleDismissCanvas = useCallback(() => {
    setCanvasDismissed(true);
  }, []);

  const handleDismissTicket = useCallback(() => {
    setTicketDismissed(true);
  }, []);

  const handleCommentClick = useCallback(
    (comment: { _id?: string; rel_x?: number; rel_y?: number }) => {
      if (comment.rel_x != null && comment.rel_y != null) {
        setFocusedComment({
          id: comment._id ?? "",
          x: comment.rel_x,
          y: comment.rel_y,
        });
        // Auto-clear after 3 seconds
        setTimeout(() => setFocusedComment(null), 3000);
      }
    },
    [],
  );

  const handleSend = async (overrideMessage?: string) => {
    const text = overrideMessage ?? inputMessage;
    if (!text.trim() || sendMessage.isPending || hasPending) return;

    const messageToSend = text;
    const attachments = [...readyAttachments];
    const messageLength = messageToSend.length;
    const messageIndex = localMessages.filter((m) => m.role === "user").length;

    const userMsg: AskPunttMessage = {
      _id: `local-user-${Date.now()}`,
      chatId,
      role: "user",
      content: messageToSend,
      attachments: attachments.length > 0 ? attachments : undefined,
      createdAt: new Date().toISOString(),
    };

    setLocalMessages((prev) => [...prev, userMsg]);
    setInputMessage("");
    clearAll();

    try {
      const result = await sendMessage.mutateAsync({
        message: messageToSend,
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Replace the optimistic user message with the server-confirmed one
      setLocalMessages((prev) => {
        const withoutOptimistic = prev.filter((m) => m._id !== userMsg._id);
        return [...withoutOptimistic, result.userMessage];
      });
      knownMessageCountRef.current = localMessages.length;
      dataAtWaitStartRef.current = dataUpdatedAt;
      setWaitingForAI(true);

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_sent", {
        chat_id: chatId,
        message_length: messageLength,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        message_index: messageIndex,
        workflow_id: result.workflowId,
      });
    } catch (error) {
      setLocalMessages((prev) => prev.filter((m) => m._id !== userMsg._id));

      const attachmentIds = attachments.map((att) => att.key);
      const chatConnectors = chatData?.chat?.enabledConnectors ?? [];

      analytics.capture("ask_puntt_message_send_error", {
        chat_id: chatId,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: chatConnectors,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to send message:", error);
    }
  };

  const chat = chatData?.chat;
  const reviewStatus = chat?.reviewStatus ?? null;

  // Sync review polling state with reviewStatus from server
  useEffect(() => {
    setReviewPolling(!!reviewStatus);
  }, [reviewStatus]);

  // Find the active skill label from the last assistant message
  const activeSkillLabel = useMemo(() => {
    for (let i = localMessages.length - 1; i >= 0; i--) {
      const msg = localMessages[i];
      if (msg.role === "assistant" && msg.activeSkill) {
        return SKILL_LABELS[msg.activeSkill];
      }
    }
    return undefined;
  }, [localMessages]);

  // Extract suggested responses from the last assistant message
  const activeSuggestedResponses = useMemo(() => {
    const lastMsg = localMessages[localMessages.length - 1];
    if (
      lastMsg?.role === "assistant" &&
      lastMsg.suggestedResponses &&
      lastMsg.suggestedResponses.length > 0
    ) {
      return lastMsg.suggestedResponses;
    }
    return null;
  }, [localMessages]);

  const showSuggestions =
    activeSuggestedResponses && !waitingForAI && !sendMessage.isPending;

  return (
    <div
      className="flex h-full flex-col overflow-hidden bg-puntt-neutral-gray-2"
      data-testid="ask-puntt-chat"
    >
      {/* Breadcrumb */}
      <Flex
        align="center"
        gap="1"
        className="shrink-0 border-b border-puntt-neutral-gray-4 bg-base-white/90 px-5 py-3.5 backdrop-blur-sm"
      >
        <Link to={askPunttRoute.to} className="puntt-link">
          <Text
            size="2"
            weight="medium"
            className="text-puntt-accent-11 hover:underline"
          >
            Ask Puntt
          </Text>
        </Link>
        {chat?.title && (
          <>
            <CaretRight size={12} className="text-puntt-neutral-gray-9" />
            <Text
              size="2"
              weight="medium"
              className="max-w-[400px] truncate text-puntt-neutral-gray-12"
            >
              {chat.title}
            </Text>
          </>
        )}
        {activeSkillLabel && (
          <Badge size="1" variant="soft" color="blue" className="ml-2">
            {activeSkillLabel}
          </Badge>
        )}
        {canvasDismissed && hasCanvasContent && (
          <Button
            size="1"
            variant="soft"
            color="blue"
            className="ml-auto cursor-pointer"
            onClick={() => setCanvasDismissed(false)}
          >
            <ArrowsOutSimple size={14} />
            <Text size="1">Show Canvas</Text>
          </Button>
        )}
      </Flex>

      {/* Main content: multi-pane layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Chat Pane */}
        <div
          className={`flex flex-col overflow-hidden ${
            use3PaneLayout
              ? "w-1/4 border-r border-puntt-neutral-gray-5"
              : activeViewTicketId || activeCanvas || isCanvasLoading
                ? "w-1/2 border-r border-puntt-neutral-gray-5"
                : "w-full"
          }`}
        >
          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6" data-testid="ask-puntt-messages">
            <div className="mx-auto max-w-[720px] space-y-4">
              {isLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="h-16 animate-pulse rounded-lg bg-puntt-neutral-gray-4"
                    />
                  ))}
                </div>
              ) : (
                <>
                  {localMessages.map((message, idx) => (
                    <ChatMessage
                      key={message._id}
                      message={message}
                      isStreaming={
                        waitingForAI &&
                        idx === localMessages.length - 1 &&
                        message.role === "assistant"
                      }
                    />
                  ))}
                  {waitingForAI &&
                    localMessages[localMessages.length - 1]?.role !==
                      "assistant" && (
                      <div className="flex items-center gap-3 rounded-2xl border border-puntt-neutral-gray-4/50 bg-base-white p-4 shadow-sm">
                        <div className="animate-thinking-pulse">
                          <RobotIcon />
                        </div>
                        <div className="flex items-baseline gap-1.5">
                          <Text
                            size="2"
                            weight="medium"
                            className="text-puntt-neutral-gray-11"
                          >
                            Puntt is thinking
                          </Text>
                          <span className="flex gap-0.5">
                            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:0ms]" />
                            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:150ms]" />
                            <span className="size-1.5 animate-bounce rounded-full bg-puntt-accent-9 [animation-delay:300ms]" />
                          </span>
                        </div>
                      </div>
                    )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input Area */}
          <div className="shrink-0 border-t border-puntt-neutral-gray-4/50 bg-puntt-neutral-gray-2 p-4">
            <div className="mx-auto max-w-[720px]">
              <div className="relative rounded-2xl border border-puntt-neutral-gray-5 bg-base-white shadow-sm transition-shadow duration-200 focus-within:border-puntt-accent-7 focus-within:shadow-md focus-within:ring-2 focus-within:ring-puntt-accent-6/20">
                {showSuggestions && (
                  <SuggestedResponses
                    suggestions={activeSuggestedResponses}
                    onSelect={(suggestion) => handleSend(suggestion)}
                    disabled={sendMessage.isPending || waitingForAI}
                  />
                )}
                <TextArea
                  data-testid="ask-puntt-chat-reply-input"
                  size="2"
                  variant="surface"
                  placeholder="Reply..."
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[60px] w-full resize-none border-none !shadow-none"
                  style={{ outline: "none", boxShadow: "none" }}
                />

                {/* Attached files preview */}
                {attachedItems.length > 0 && (
                  <Flex gap="2" wrap="wrap" className="px-3 pb-1">
                    {attachedItems.map((item, idx) => (
                      <Flex
                        key={item.id}
                        align="center"
                        gap="1"
                        className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                      >
                        <Text
                          size="1"
                          className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                        >
                          {item.file.name}
                          {item.status === "pending" && " (uploading…)"}
                          {item.status === "error" && " (failed)"}
                        </Text>
                        <button
                          onClick={() => removeFile(idx)}
                          className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                        >
                          ×
                        </button>
                      </Flex>
                    ))}
                  </Flex>
                )}

                <Flex justify="between" align="center" className="px-3 py-1.5">
                  <InputPlusMenu
                    onFilesSelected={addFiles}
                    onScreenshotCaptured={addFile}
                    enabledConnectors={connectorsArrayToRecord(
                      chat?.enabledConnectors,
                    )}
                    connectorsDisabled
                  />

                  <Button
                    data-testid="ask-puntt-chat-send"
                    size="2"
                    variant={inputMessage.trim() ? "solid" : "ghost"}
                    color={inputMessage.trim() ? "blue" : "gray"}
                    disabled={
                      !inputMessage.trim() ||
                      sendMessage.isPending ||
                      hasPending ||
                      waitingForAI
                    }
                    onClick={() => handleSend()}
                    className="cursor-pointer transition-all duration-200"
                  >
                    <ArrowUp size={14} weight="bold" />
                  </Button>
                </Flex>
              </div>
            </div>
          </div>
        </div>

        {/* Right Pane(s) */}
        {use3PaneLayout ? (
          <>
            {/* 3-pane: Label Sheet + Artwork */}
            <div className="w-3/8 animate-canvas-slide-in border-r border-puntt-neutral-gray-5">
              {viewTicketLoading ? (
                <div className="flex h-full items-center justify-center bg-base-white">
                  <Text size="2" className="text-puntt-neutral-gray-9">
                    Loading...
                  </Text>
                </div>
              ) : (
                <AssetViewerPane
                  title="Label Sheet"
                  assets={contextFiles}
                  highlightPosition={
                    focusedComment
                      ? {
                          x: focusedComment.x,
                          y: focusedComment.y,
                          page: focusedComment.page,
                        }
                      : null
                  }
                />
              )}
            </div>
            <div className="relative w-3/8 animate-canvas-slide-in">
              {viewTicketLoading ? (
                <div className="flex h-full items-center justify-center bg-base-white">
                  <Text size="2" className="text-puntt-neutral-gray-9">
                    Loading...
                  </Text>
                </div>
              ) : (
                <>
                  <AssetViewerPane
                    title="Artwork"
                    assets={latestReviewFiles}
                    reviewStatus={reviewStatus}
                    highlightPosition={
                      focusedComment
                        ? {
                            x: focusedComment.x,
                            y: focusedComment.y,
                            page: focusedComment.page,
                          }
                        : null
                    }
                  />
                  {viewTicket && (
                    <TicketDetailsDrawer
                      ticket={viewTicket}
                      comments={viewComments ?? []}
                      isOpen={ticketDetailsOpen}
                      onToggle={() => setTicketDetailsOpen((o) => !o)}
                      onCommentClick={handleCommentClick}
                      activeCommentId={focusedComment?.id}
                    />
                  )}
                </>
              )}
            </div>
          </>
        ) : activeViewTicketId ? (
          <div className="w-1/2 animate-canvas-slide-in">
            <TicketViewPane
              ticketId={activeViewTicketId}
              onDismiss={handleDismissTicket}
              reviewStatus={reviewStatus}
            />
          </div>
        ) : activeCanvas ? (
          <div className="w-1/2 animate-canvas-slide-in" data-testid="ask-puntt-canvas-area">
            <CanvasPane
              canvas={activeCanvas.canvas}
              onDismiss={handleDismissCanvas}
              skillName={
                activeCanvas.skill
                  ? SKILL_LABELS[activeCanvas.skill]
                  : undefined
              }
              chatId={chatId}
            />
          </div>
        ) : isCanvasLoading ? (
          <div className="w-1/2 animate-canvas-slide-in">
            <CanvasLoadingPane />
          </div>
        ) : null}
      </div>
    </div>
  );
}
